import createDecorator from 'final-form-focus'
import PropTypes from 'prop-types'
import React from 'react'
import { Form, FormSpy, Field } from 'react-final-form'

import TextField from 'components/_form/TextField'
import TextFieldWithSuggestions from 'components/_form/TextFieldWithSuggestions'
import OrderFieldInfo from 'components/_special/OrderFieldInfo'
import usePersInfoSubmit from 'forms/hooks/usePersInfoSubmit'
import {
  phoneMaskJapanPart1,
  phoneMaskJapanPart2,
  phoneMaskJapanPart3,
  postcodeMaskJapan,
} from 'forms/masks'
import {
  composeValidators,
  errorFromDict,
  errorCustom,
  required,
  maxLength,
  isEmail,
  minLength,
  onlyKatakana,
  withoutNumbers,
  exactLength,
} from 'forms/validations'
import { Col } from 'styles/components/layout'
import { getJapaneseAddress } from 'utils/api'
import { scrollToErrorInput } from 'utils/client/scroll'


import ScrollableRules from './ScrollableRules'
import { RowStyled, PhoneFieldColWrapper, BoxPhone } from './styles'

const focusOnError = createDecorator(undefined, scrollToErrorInput)

const OrderPersInfoJapanForm = React.forwardRef((props, ref) => {
  const {
    onChange,
    onSubmit,
    id,
    errorsDict,
    optionsCountry,
    optionsPhoneCode,
    initialValues,
    texts,
    rules,
    isDisabled,
    deliveryType,
  } = props
  const [initVal] = React.useState(() => initialValues)

  const onPersInfoSubmit = usePersInfoSubmit({ onChange, onSubmit })
  return (
    <Form
      onSubmit={onPersInfoSubmit}
      decorators={[focusOnError]}
      subscription={{
        submitting: true,
        pristine: true,
      }}
      initialValues={initVal}
      render={({ handleSubmit, submitting, form: { change, batch } }) => (
        <form id={id} onSubmit={handleSubmit} noValidate={true} ref={ref}>
          <div>
            <RowStyled>
              <Col width={{ xs: 1, md: 7 / 12, lg: 1 / 2 }}>
                <TextField
                  autoComplete="off"
                  placeholder={texts.fields.name_kanji.title}
                  name="name2"
                  isDisabled={isDisabled}
                  validate={composeValidators([
                    errorCustom(errorsDict.contact_details_required, required),
                    errorCustom(errorsDict.only_letters, withoutNumbers),
                  ])}
                />
              </Col>
              <OrderFieldInfo>{texts.fields.name_kanji.hint}</OrderFieldInfo>
            </RowStyled>
            <RowStyled>
              <Col width={{ xs: 1, md: 7 / 12, lg: 1 / 2 }}>
                <TextField
                  autoComplete="name"
                  placeholder={texts.fields.name.title}
                  name="name"
                  isDisabled={isDisabled}
                  validate={composeValidators([
                    errorCustom(errorsDict.contact_details_required, required),
                    errorCustom(errorsDict.only_katakana, onlyKatakana),
                  ])}
                />
              </Col>
              <OrderFieldInfo>{texts.fields.name.hint}</OrderFieldInfo>
            </RowStyled>

            <Field
              component="input"
              type="hidden"
              name="country"
              value={optionsCountry[0].value}
            />
            <RowStyled>
              <Col width={{ xs: 1, md: 7 / 12, lg: 1 / 2 }}>
                <TextFieldWithSuggestions
                  key={deliveryType}
                  placeholder={texts.fields.postcode.title}
                  name="postcode"
                  mask={postcodeMaskJapan}
                  maskPlaceholder=""
                  isDisabled={isDisabled}
                  validate={composeValidators([
                    errorCustom(errorsDict.contact_details_required, required),
                    errorCustom(errorsDict.postcode_max_length, exactLength(7)),
                  ])}
                  fetchApi={getJapaneseAddress}
                  onChange={() => {
                    batch(() => {
                      change('region', null)
                      change('city', null)
                    })
                  }}
                  onSelect={({
                    postcode,
                    prefecture,
                    city,
                    town_area,
                  } = {}) => {
                    batch(() => {
                      change('postcode', postcode)
                      change('region', prefecture)
                      change('city', city && town_area && `${city}${town_area}`)
                    })
                  }}
                />
              </Col>
              <OrderFieldInfo>{texts.fields.postcode.hint}</OrderFieldInfo>
            </RowStyled>

            <RowStyled>
              <Col width={{ xs: 1, md: 7 / 12, lg: 1 / 2 }}>
                <TextField
                  key={deliveryType}
                  autoComplete="address-level1"
                  placeholder={texts.fields.prefecture.title}
                  name="region"
                  isDisabled={isDisabled}
                  validate={errorCustom(
                    errorsDict.contact_details_required,
                    required,
                  )}
                />
              </Col>
              <OrderFieldInfo>{texts.fields.prefecture.hint}</OrderFieldInfo>
            </RowStyled>

            <RowStyled>
              <Col width={{ xs: 1, md: 7 / 12, lg: 1 / 2 }}>
                <TextField
                  key={deliveryType}
                  autoComplete="address-level2"
                  placeholder={texts.fields.city.title}
                  name="city"
                  isDisabled={isDisabled}
                  validate={composeValidators([
                    errorCustom(errorsDict.contact_details_required, required),
                    errorCustom(errorsDict.only_letters, withoutNumbers),
                  ])}
                />
              </Col>
              <OrderFieldInfo>{texts.fields.city.hint}</OrderFieldInfo>
            </RowStyled>

            <RowStyled>
              <Col width={{ xs: 1, md: 7 / 12, lg: 1 / 2 }}>
                <TextField
                  key={deliveryType}
                  placeholder={texts.fields.district.title}
                  name="district"
                  isDisabled={isDisabled}
                  validate={errorCustom(
                    errorsDict.contact_details_required,
                    required,
                  )}
                />
              </Col>
              <OrderFieldInfo>{texts.fields.district.hint}</OrderFieldInfo>
            </RowStyled>

            <RowStyled>
              <Col width={{ xs: 1, md: 7 / 12, lg: 1 / 2 }}>
                <TextField
                  key={deliveryType}
                  placeholder={texts.fields.address.title}
                  name="address"
                  isDisabled={isDisabled}
                  validate={errorCustom(
                    errorsDict.contact_details_required,
                    required,
                  )}
                />
              </Col>
              <OrderFieldInfo>{texts.fields.address.hint}</OrderFieldInfo>
            </RowStyled>
          </div>

          <RowStyled>
            <PhoneFieldColWrapper width={{ xs: 1, md: 7 / 12, lg: 1 / 2 }}>
              <Field
                component="input"
                type="hidden"
                name="phone_code"
                value={optionsPhoneCode[0].value}
              />
              <BoxPhone>
                <TextField
                  component="input"
                  type="tel"
                  name="phone1"
                  placeholder={texts.fields.phone.title}
                  mask={phoneMaskJapanPart1}
                  maskPlaceholder=""
                  validate={composeValidators([
                    errorCustom(errorsDict.phone_length1, minLength(2)),
                    errorCustom(errorsDict.phone_length1, maxLength(5)),
                  ])}
                />
              </BoxPhone>
              <BoxPhone>
                <TextField
                  component="input"
                  type="tel"
                  name="phone2"
                  mask={phoneMaskJapanPart2}
                  maskPlaceholder=""
                  validate={composeValidators([
                    errorCustom(errorsDict.phone_length2, minLength(1)),
                    errorCustom(errorsDict.phone_length2, maxLength(4)),
                  ])}
                />
              </BoxPhone>
              <BoxPhone noMargin={true}>
                <TextField
                  component="input"
                  type="tel"
                  name="phone3"
                  mask={phoneMaskJapanPart3}
                  maskPlaceholder=""
                  validate={composeValidators([
                    errorCustom(errorsDict.phone_length3, minLength(4)),
                    errorCustom(errorsDict.phone_length3, maxLength(4)),
                  ])}
                />
              </BoxPhone>
            </PhoneFieldColWrapper>
            <OrderFieldInfo>{texts.fields.phone.hint}</OrderFieldInfo>
          </RowStyled>

          <RowStyled>
            <Col width={{ xs: 1, md: 7 / 12, lg: 1 / 2 }}>
              <TextField
                autoComplete="email"
                placeholder={texts.fields.email.title}
                type="email"
                name="email"
                isDisabled={isDisabled}
                validate={composeValidators([
                  errorCustom(errorsDict.contact_details_required, required),
                  errorFromDict(errorsDict, isEmail),
                ])}
              />
            </Col>
            <OrderFieldInfo>{texts.fields.email.hint}</OrderFieldInfo>
          </RowStyled>

          <ScrollableRules
            title={texts.agreement}
            agreement={texts.fields.agreement.pattern}
            haveToAgree={errorsDict.have_to_agree}
            isDisabled={isDisabled}
            {...rules}
          />

          <Field
            name="submit"
            type="submit"
            component="input"
            hidden={true}
            disabled={submitting || isDisabled}
          />

          <FormSpy
            subscription={{
              values: true,
              invalid: true,
              active: true,
              touched: true,
            }}
            onChange={({ values, invalid, active, touched }) => {
              if (!!values.phone1 && !!values.phone2 && !!values.phone3) {
                const newPhone = `${values.phone1}-${values.phone2}-${values.phone3}`
                if (values.phone !== newPhone) change('phone', newPhone)
              }
              return onChange({ values, invalid, active, touched })
            }}
          />
        </form>
      )}
    />
  )
})

OrderPersInfoJapanForm.propTypes = {
  errorsDict: PropTypes.object.isRequired,
  id: PropTypes.string.isRequired,
  initialValues: PropTypes.object.isRequired,
  optionsCountry: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ).isRequired,
  optionsPhoneCode: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ).isRequired,
  texts: PropTypes.object.isRequired,
  rules: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  beforeCardContent: PropTypes.node,
  isDisabled: PropTypes.bool,
  deliveryType: PropTypes.number,
}

OrderPersInfoJapanForm.defaultProps = {
  isDisabled: false,
}

export default React.memo(OrderPersInfoJapanForm)
